import type { AppointmentStatus } from '@bb/domain/dist/types';
import type { GetPermissionsAppointmentDTO } from '@bb/domain/dist/types/dto/AppointmentController/getPermissionsAppointment';
import { AppointmentController, ShowingCalendarController } from 'api';
import { message } from 'components';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { startOfDay } from 'date-fns';
import type moment from 'moment';
import { featureEnabled } from 'utils/featureFlagBrowserClient';
import errorReporter from '@app/errorReporter';
import type { Listing } from 'api/ListingController/getListing';
import { DEFAULT_STATUS_FILTERS } from '../constants';
import { PAGE_SIZE } from '../Tables/constants';
import type {
  BuyerTableData,
  ListingTableData,
  InPersonTableData,
  VirtualTableData,
  AppointmentTypes,
  StatusCounts,
} from '../types';
import { AgentCalendarMenu, ListingCalendarMenu } from '../types';
import { getTourItemDateToShow } from '../../TourEditPage/utils';

type UseFetchAppointmentsProps = {
  searchText?: string | undefined;
  date?: [moment.Moment, moment.Moment | undefined];
  statuses?: AppointmentStatus[];
  pendingStatus?: boolean;
  selectedMenuOption: AgentCalendarMenu | ListingCalendarMenu;
  setSelectedAppointments: (apptIds: string[]) => void;
  page: number;
  listing: Listing;
  listingId?: string;
  hasVirtualTour?: boolean;
  shouldFetch: boolean;
  setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
  setPermissionsMap: React.Dispatch<
    React.SetStateAction<Record<string, GetPermissionsAppointmentDTO>>
  >;
  setStatusCount: React.Dispatch<React.SetStateAction<StatusCounts>>;
};

export const useFetchAppointments = ({
  date,
  page,
  listing,
  listingId,
  searchText,
  selectedMenuOption,
  setSelectedAppointments,
  statuses,
  hasVirtualTour,
  shouldFetch,
  pendingStatus,
  setIsFetching,
  setPermissionsMap,
  setStatusCount,
}: UseFetchAppointmentsProps) => {
  const history = useHistory();
  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');
  const [buyerAppointments, setBuyerAppointments] =
    React.useState<BuyerTableData>([]);
  const [listingAppointments, setListingAppointments] =
    React.useState<ListingTableData>([]);
  const [inPersonAppointments, setInPersonAppointments] =
    React.useState<InPersonTableData>([]);
  const [virtualAppointments, setVirtualAppointments] =
    React.useState<VirtualTableData>([]);

  const [buyerActionCount, setBuyerActionCount] = React.useState(null);
  const [listingActionCount, setListingActionCount] = React.useState(null);
  const [inPersonActionCount, setInPersonActionCount] = React.useState(null);

  const [totalShowingsCount, setTotalShowingsCount] = React.useState(0);

  /** Used to prevent race conditions */
  const lastFetchToken = React.useRef([]);
  const hideHandler = React.useRef(null);

  const setAppointmentList = React.useCallback(
    (
      list: AppointmentTypes,
      setList: React.Dispatch<React.SetStateAction<AppointmentTypes>>,
    ) => {
      const menuOptionToSetList: Record<
        typeof selectedMenuOption,
        React.Dispatch<React.SetStateAction<AppointmentTypes>>
      > = {
        [AgentCalendarMenu.Buyer]: setBuyerAppointments,
        [AgentCalendarMenu.Listing]: setListingAppointments,
        [ListingCalendarMenu.InPerson]: setInPersonAppointments,
        [ListingCalendarMenu.Virtual]: setVirtualAppointments,
      };

      setSelectedAppointments([]);

      if (
        list.length === 0 &&
        setList === menuOptionToSetList[selectedMenuOption]
      ) {
        message.info('No requests available');
      }
      setList(list);
    },
    [selectedMenuOption],
  );

  const processedStatuses: AppointmentStatus[] = React.useMemo(() => {
    // Ensure we're not fetching all statuses like removed when filter is empty
    if (!statuses?.length && !pendingStatus) {
      if (selectedMenuOption === AgentCalendarMenu.Buyer) {
        return [...DEFAULT_STATUS_FILTERS, 'draft'];
      }
      return DEFAULT_STATUS_FILTERS;
    }
    // Attempted showings are shown as new/pending to buyer
    if (selectedMenuOption === AgentCalendarMenu.Buyer && pendingStatus) {
      return [...statuses, 'attempted', 'new'];
    }
    return statuses;
  }, [statuses, selectedMenuOption]);

  const fetchAppointments = React.useCallback(
    async (esDelay = 0) => {
      if (!shouldFetch) return;
      const [startDate, endDate] = date || [];

      setIsFetching(true);
      if (hideHandler.current) hideHandler.current();
      lastFetchToken.current = [];
      hideHandler.current = message.loading('Loading showing requests');
      const currentToken = lastFetchToken.current;
      const now = new Date();
      const hasPassed = (timeEnd: string) => now > new Date(timeEnd);
      let totalCount = 0;

      try {
        if (esDelay && selectedMenuOption === AgentCalendarMenu.Buyer) {
          await new Promise(resolve => setTimeout(resolve, esDelay));
        }

        if (
          selectedMenuOption === AgentCalendarMenu.Buyer ||
          selectedMenuOption === AgentCalendarMenu.Listing
        ) {
          const agentPromises: [
            ReturnType<
              (typeof AppointmentController)['getAppointmentsForCoop']
            >,
            ReturnType<(typeof ShowingCalendarController)['appointments']>,
          ] = [Promise.resolve(undefined), Promise.resolve(undefined)];
          if (
            buyerActionCount === null ||
            selectedMenuOption === AgentCalendarMenu.Buyer
          ) {
            agentPromises[0] = AppointmentController.getAppointmentsForCoop({
              useV2Logic: true,
              sortOrder: chronologicalShowingsEnabled ? 'asc' : 'desc',
              statuses: processedStatuses,
              searchText,
              startDate,
              endDate,
              page,
              includeConfirmedPendingAppointment:
                pendingStatus && !statuses.includes('confirm'),
              excludeConfirmedPendingAppointment:
                !pendingStatus && statuses.includes('confirm'),
              includeSignatures: true,
              includeActionCount: true,
              docType: ['appointment', 'tourItem'],
              includeSupplementaryTourItems: true,
              statusFiltered: Boolean(statuses.length),
            });
          }
          if (
            listingActionCount === null ||
            selectedMenuOption === AgentCalendarMenu.Listing
          ) {
            agentPromises[1] = ShowingCalendarController.appointments({
              agent: 'listing',
              dateFrom: startDate.toDate(),
              dateTo: endDate?.toDate(),
              pageNumber: page,
              searchText,
              statuses: processedStatuses as AppointmentStatus[],
              sortAsc: chronologicalShowingsEnabled ? 'true' : 'false',
              withPermissions: 'true',
              withSignatures: 'true',
              withActionCount: 'true',
              includeConfirmedPendingAppointment:
                pendingStatus && !statuses.includes('confirm')
                  ? 'true'
                  : 'false',
              excludeConfirmedPendingAppointment:
                !pendingStatus && statuses.includes('confirm')
                  ? 'true'
                  : 'false',
            });
          }

          const [buyerResult, listingResult] = await Promise.all(agentPromises);
          if (currentToken !== lastFetchToken.current) return;

          if (buyerResult) {
            setAppointmentList(
              [
                ...buyerResult.appointments,
                ...(buyerResult.supplementaryItems || []),
              ].map(appt => ({
                ...appt,
                passed: appt.timeEnd ? hasPassed(appt.timeEnd) : undefined,
                ...(appt.status === 'draft' && {
                  // @ts-expect-error: listing.location is string | number, while expected to be string
                  date: getTourItemDateToShow(appt),
                }),
              })),
              setBuyerAppointments,
            );
            setBuyerActionCount(buyerResult.actionCount);
          }
          if (listingResult) {
            setAppointmentList(
              listingResult.appointments.map(appt => ({
                ...appt,
                passed: hasPassed(appt.timeEnd),
              })),
              setListingAppointments,
            );
            setListingActionCount(listingResult.actionCount);
            setPermissionsMap(listingResult.permissions);
          }
          if (selectedMenuOption === AgentCalendarMenu.Buyer) {
            totalCount = buyerResult.count;
          } else {
            totalCount = listingResult.count;
          }
        } else if (
          listing &&
          (selectedMenuOption === ListingCalendarMenu.InPerson ||
            selectedMenuOption === ListingCalendarMenu.Virtual)
        ) {
          const listingPromises: [
            ReturnType<
              (typeof AppointmentController)['paginateAppointmentsByListing']
            >?,
            ReturnType<
              (typeof AppointmentController)['paginateVirtualToursByListing']
            >?,
          ] = [];
          if (
            inPersonActionCount === null ||
            selectedMenuOption === ListingCalendarMenu.InPerson
          ) {
            listingPromises[0] =
              AppointmentController.paginateAppointmentsByListing({
                listingId,
                page,
                filter: 'all',
                statuses: chronologicalShowingsEnabled
                  ? processedStatuses
                  : undefined,
                dateFrom:
                  chronologicalShowingsEnabled && startDate
                    ? startDate.startOf('day').toISOString()
                    : undefined,
                limit: PAGE_SIZE,
                sortBy: chronologicalShowingsEnabled ? 'time ASC' : 'time DESC',
                includeSignatures: true,
              });
          }
          if (
            hasVirtualTour &&
            selectedMenuOption === ListingCalendarMenu.Virtual
          ) {
            listingPromises[1] =
              AppointmentController.paginateVirtualToursByListing({
                listingId,
                page,
                limit: PAGE_SIZE,
                sortField: 'startTime',
                isDesc: !chronologicalShowingsEnabled,
                withCoopData: true,
              });
          }
          const [inPersonResult, virtualResult] =
            await Promise.all(listingPromises);
          if (currentToken !== lastFetchToken.current) return;
          if (inPersonResult) {
            if (selectedMenuOption === ListingCalendarMenu.InPerson) {
              totalCount = inPersonResult.totalCount;
              setStatusCount(inPersonResult.countGroupByStatus);

              setAppointmentList(
                inPersonResult.appointments.map(appt => ({
                  ...appt,
                  listing: { ...appt.listing, thumbnail: listing.thumbnail },
                  passed: hasPassed(appt.timeEnd),
                })),
                setInPersonAppointments,
              );
            }
            setInPersonActionCount(inPersonResult.needsActionCount);
          }
          if (virtualResult) {
            totalCount = virtualResult.virtualToursCount;
            setStatusCount(virtualResult.virtualToursStatusCount);
            setAppointmentList(
              virtualResult.virtualTours.map(vt => ({
                ...vt,
                listing: { thumbnail: listing.thumbnail },
                coop: virtualResult.coopData[vt.coop.id]
                  ? { ...vt.coop, ...virtualResult.coopData[vt.coop.id] }
                  : vt.coop,
                // Adding these props makes it easier to group appointments by date
                date: startOfDay(new Date(vt.startTime)).toISOString(),
                time: vt.startTime,
              })),
              setVirtualAppointments,
            );
          } else if (
            !hasVirtualTour &&
            selectedMenuOption === ListingCalendarMenu.Virtual
          ) {
            totalCount = 0;
            setStatusCount({});
          }
        }
        setTotalShowingsCount(totalCount);
        setIsFetching(false);
        hideHandler.current();
      } catch (err) {
        if (err.response?.data.type === 'FORBIDDEN_ACTION') {
          message.error(
            'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.',
          );
        } else {
          message.error('Something went wrong. Please try again later.');
        }
        if (listingId) history.push(`/listing/${listingId}/view`);
        errorReporter.error(err);
        setIsFetching(false);
        hideHandler.current();
      }
    },
    [
      processedStatuses,
      date,
      page,
      listing,
      listingId,
      statuses,
      searchText,
      setIsFetching,
      selectedMenuOption,
      hasVirtualTour,
      shouldFetch,
      setStatusCount,
      setAppointmentList,
      setPermissionsMap,
      chronologicalShowingsEnabled,
      buyerActionCount,
      history,
      inPersonActionCount,
      listingActionCount,
      pendingStatus,
    ],
  );

  React.useEffect(
    () => () => {
      lastFetchToken.current = [];
    },
    [],
  );

  React.useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  return {
    buyerAppointments,
    listingAppointments,
    inPersonAppointments,
    virtualAppointments,
    buyerActionCount,
    listingActionCount,
    inPersonActionCount,
    refetch: fetchAppointments,
    totalShowingsCount,
  };
};
